import { Check, Circle } from '@mui/icons-material';
import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useEffect, useState } from 'react';
import { CaptionText } from '../../shared/typography/PrimaryTypography';
import { validatePasswordInput } from '../../../lib/validation/text_validation';
import { PasswordValidationResult } from '../../../@types/shared';

interface PasswordValidationRule {
  is_valid: boolean;
  value: string;
}

interface PasswordValidationRuleListProps {
  password: string;
  first_name: string;
  last_name: string;
  username: string;
  is_blur_event: boolean;
}

/**
 * Component displaying the password rules required.
 * @param {string} password The password string to validate.
 * @param {string} first_name The user's first name, used to ensure the password does not contain first name
 * @param {string} last_name The user's last name, used to ensure the password does not contain last name
 * @param {string} username The user's  username, used to ensure the password does not contain username
 * @param {boolean} is_blur_event The flag used to determine if the validation is triggered by a an input field 'blur' event
 * @returns {JSX.Element}
 */
const PasswordValidationRuleList = ({
  password,
  first_name,
  last_name,
  username,
  is_blur_event
}: PasswordValidationRuleListProps): JSX.Element => {
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasFirstName, setHasFirstName] = useState<boolean>(true);
  const [hasLastName, setHasLastName] = useState<boolean>(true);
  const [hasUsername, setHasUsername] = useState<boolean>(true);
  const [isRequiredLength, setIsRequiredLength] = useState<boolean>(false);

  useEffect(() => {
    const validatedPassword: PasswordValidationResult = validatePasswordInput(
      password,
      first_name,
      last_name,
      username
    );
    validatedPassword.has_lowercase ? setHasLowercase(true) : setHasLowercase(false);
    validatedPassword.has_uppercase ? setHasUppercase(true) : setHasUppercase(false);
    validatedPassword.has_number ? setHasNumber(true) : setHasNumber(false);
    validatedPassword.has_first_name && first_name.trim()
      ? setHasFirstName(true)
      : setHasFirstName(false);
    validatedPassword.has_last_name && last_name.trim()
      ? setHasLastName(true)
      : setHasLastName(false);
    validatedPassword.has_username && username.trim()
      ? setHasUsername(true)
      : setHasUsername(false);
    validatedPassword.is_required_length ? setIsRequiredLength(true) : setIsRequiredLength(false);
  }, [password, first_name]);

  const validationRules: Array<PasswordValidationRule> = [
    {
      is_valid: isRequiredLength,
      value: 'Use 12 characters or more'
    },
    {
      is_valid: hasUppercase && hasLowercase,
      value: 'Mix lower and uppercase letters'
    },
    {
      is_valid: hasNumber,
      value: 'Use at least one number'
    },
    {
      is_valid: !hasFirstName && !hasLastName && !hasUsername,
      value: 'Do not use your name or email'
    }
  ];

  return (
    <List>
      {validationRules.map((rule) => (
        <ListItem key={rule.value} disablePadding>
          <ListItemIcon sx={{ minWidth: 20 }}>
            {rule.is_valid ? (
              <Check sx={{ fontSize: '0.9rem', color: '#9e9e9e' }} />
            ) : (
              <Circle
                sx={
                  is_blur_event ? { fontSize: '0.6rem', color: '#d32f2f' } : { fontSize: '0.6rem' }
                }
              />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <CaptionText
                sx={!rule.is_valid && is_blur_event ? { color: '#d32f2f' } : { color: '#9e9e9e' }}
              >
                {rule.value}
              </CaptionText>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

export default PasswordValidationRuleList;
